import { useEffect, useRef, useState } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import { useQuery } from '@tanstack/react-query'

import {
  fetchDailyTimeEntries,
  getDailyTimeEntriesQueryKey,
} from './useDailyTimeEntries'
import { getCloneDailyDurationBudget } from '../utils/cloneDailyDuration'

type CloneForm = {
  duration: number | null
  start: Date | null
  stop: Date | null
}

type Options<T extends CloneForm> = {
  enabled: boolean
  dateKey: string | null
  userId?: string | number
  excludeEntryId?: string | number
  excludeGroupId?: string | number | null
  minMinutes: number | boolean
  formData: T
  setFormData: Dispatch<SetStateAction<T>>
}

export function useCloneDailyDuration<T extends CloneForm>({
  enabled,
  dateKey,
  userId,
  excludeEntryId,
  excludeGroupId,
  minMinutes,
  formData,
  setFormData,
}: Options<T>) {
  const scope = `${userId ?? 'self'}:${dateKey}`
  const noticeSequence = useRef(0)
  const [notice, setNotice] = useState<{
    scope: string
    id: number
    originalDurationHours: number
    adjustedDurationHours: number
    adjustmentReason: 'daily' | 'day'
  } | null>(null)
  const query = useQuery({
    queryKey: getDailyTimeEntriesQueryKey(dateKey, userId),
    queryFn: () => fetchDailyTimeEntries(dateKey!, userId),
    enabled: enabled && Boolean(dateKey),
    refetchOnMount: 'always',
  })
  // MIRA-4223: use the selected user's complete day, not the visible grid.
  // Exclude the entry/group being edited; a clone still counts its source.
  const budget =
    enabled && query.data
      ? getCloneDailyDurationBudget(
          query.data.filter(
            entry =>
              (excludeEntryId == null ||
                String(entry.id) !== String(excludeEntryId)) &&
              (excludeGroupId == null ||
                String(entry.group_id) !== String(excludeGroupId))
          ),
          minMinutes
        )
      : null
  const remainingHours = budget?.remainingHours
  const ready = Boolean(dateKey && query.isSuccess && !query.isFetching)

  useEffect(() => {
    if (!enabled || !ready || remainingHours == null) return
    if (formData.duration == null || formData.duration <= remainingHours) return

    setNotice({
      scope,
      id: ++noticeSequence.current,
      originalDurationHours: formData.duration,
      adjustedDurationHours: remainingHours,
      adjustmentReason: 'daily',
    })
    setFormData(previous => {
      if (previous.duration == null || previous.duration <= remainingHours)
        return previous
      return {
        ...previous,
        duration: remainingHours,
        stop: previous.start
          ? new Date(previous.start.getTime() + remainingHours * 3600000)
          : previous.stop,
      }
    })
  }, [enabled, ready, remainingHours, formData.duration, scope, setFormData])

  // Capture the attempted value before the field normalizes it. Otherwise a
  // manual edit can be silently clamped before the daily-limit effect sees it.
  const constrainDuration = (requested: number | null, dayMax: number) => {
    const maximum = Math.min(dayMax, remainingHours ?? Infinity)
    if (requested != null && requested > maximum) {
      setNotice({
        scope,
        id: ++noticeSequence.current,
        originalDurationHours: requested,
        adjustedDurationHours: maximum,
        adjustmentReason:
          (remainingHours ?? Infinity) <= dayMax ? 'daily' : 'day',
      })
      return maximum
    }
    if (notice?.scope !== scope || requested !== notice.adjustedDurationHours) {
      setNotice(null)
    }
    return requested
  }

  return {
    adjusted: notice?.scope === scope,
    noticeId: notice?.scope === scope ? notice.id : 0,
    adjustedDurationHours:
      notice?.scope === scope ? notice.adjustedDurationHours : null,
    adjustmentReason:
      notice?.scope === scope ? notice.adjustmentReason : undefined,
    constrainDuration,
    originalDurationHours:
      notice?.scope === scope ? notice.originalDurationHours : null,
    remainingHours,
    totalHours: budget?.totalHours,
    error: enabled && query.isError,
    blocked:
      enabled &&
      (!ready ||
        remainingHours == null ||
        remainingHours <= 0 ||
        (formData.duration != null && formData.duration > remainingHours)),
  }
}
